import { Button } from "@lumi/ui-headless";
import { Volume2, VolumeX } from "lucide-react";
import type { ReactElement } from "react";
import React from "react";

export type VolumeControlProps = Readonly<{
  videoRef: React.RefObject<HTMLVideoElement | null>;
}>;

type HorizontalSliderProps = Readonly<{
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (value: number) => void;
  ariaLabel?: string;
}>;

function HorizontalSlider({ value, min, max, step, onChange, ariaLabel }: HorizontalSliderProps): ReactElement {
  const percentage = ((value - min) / (max - min)) * 100;

  return (
    <div className="h-1.5 w-32 relative flex items-center">
      {/* Visual track - centered within the wider hover area */}
      <div className="h-1.5 w-full bg-neutral-700/60 rounded-full">
        {/* Fill */}
        <div className="h-full bg-lumi-accent rounded-full absolute left-0" style={{ width: `${percentage}%` }} />
        {/* Thumb */}
        <div
          className="absolute w-4 h-4 bg-white rounded-full border-2 border-neutral-900 shadow-sm transform -translate-y-1/2"
          style={{
            top: "50%",
            left: `calc(${percentage}% - 10px)`,
          }}
        />
      </div>

      {/* Native range input for interaction - covers the full wider area */}
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        aria-label={ariaLabel}
        className="absolute inset-0 opacity-0 cursor-pointer"
      />
    </div>
  );
}

export function VolumeControl({ videoRef }: VolumeControlProps): ReactElement {
  const [muted, setMuted] = React.useState<boolean>(false);
  const [volume, setVolume] = React.useState<number>(1);

  // Sync from video element
  React.useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const apply = () => {
      setMuted(Boolean(v.muted));
      // clamp volume 0..1
      const vol = typeof v.volume === "number" ? Math.min(1, Math.max(0, v.volume)) : 1;
      setVolume(vol);
    };
    apply();
    const onChange = () => apply();
    v.addEventListener("volumechange", onChange);
    return () => v.removeEventListener("volumechange", onChange);
  }, [videoRef]);

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    try {
      v.muted = !v.muted;
      // state will sync via event listener
    } catch {
      // ignore
    }
  };

  const onVolumeChange = (nextPct: number) => {
    const v = videoRef.current;
    if (!v) return;
    const clamped = Math.min(100, Math.max(0, Number.isFinite(nextPct) ? nextPct : 0));
    const next = clamped / 100;
    try {
      v.volume = next;
      if (next > 0 && v.muted) v.muted = false;
    } catch {
      // ignore
    }
  };

  const pct = Math.round(volume * 100);
  // When muted, show the slider thumb at the bottom (0%) but remember the actual volume
  const displayValue = muted ? 0 : pct;

  return (
    <div className="relative group flex items-center">
      <Button aria-label={muted ? "Unmute" : "Mute"} onClick={toggleMute} variant="ghost-sm">
        {muted || pct === 0 ? <VolumeX size={20} /> : <Volume2 size={20} />}
      </Button>

      {/* Horizontal Volume Slider - Hidden by default, shown on hover */}
      {/* Extended hover area includes the space between icon and slider with generous padding */}
      <div className="absolute right-full top-1/2 -translate-y-1/2 pr-2 opacity-0 group-hover:opacity-100 transition-[opacity] duration-200 pointer-events-none group-hover:pointer-events-auto">
        <div className="flex items-center justify-end w-36 py-8 pl-2">
          <HorizontalSlider
            value={displayValue}
            min={0}
            max={100}
            step={1}
            ariaLabel="Volume"
            onChange={onVolumeChange}
          />
        </div>
      </div>
    </div>
  );
}
